import { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CopyButton } from '@/components/ui/copy-button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { dateOnly, timeOnly } from '@/lib/datetime';

import type { AmostraDetalhe, BucketSerie, StatusAlvo } from '../../../api/monitoramento';
import { useAmostrasDoAlvo } from '../../../hooks/useMonitoramentoQuery';
import { MonitoramentoStatusPill } from './MonitoramentoStatusPill';

export interface BucketAmostrasDialogProps {
  alvo: string | null;
  bucket: BucketSerie | null;
  /** Início do bucket; se ausente usamos `bucket.inicio_bucket`. */
  inicioBucket: Date | null;
  /** Fim do bucket; se ausente, calcula como inicio + 1h. */
  fimBucket: Date | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Drill-in da uptime bar: lista as amostras cruas (`integracao_amostra`) que
 * compõem o bucket clicado. Mantém os agregados do bucket no topo (mesma
 * informação que o Tooltip do hover, para paridade com mobile).
 */
export function BucketAmostrasDialog({
  alvo,
  bucket,
  inicioBucket,
  fimBucket,
  open,
  onOpenChange,
}: BucketAmostrasDialogProps) {
  const inicioIso = inicioBucket?.toISOString() ?? null;
  const fimIso = fimBucket?.toISOString() ?? null;

  const query = useAmostrasDoAlvo(alvo, inicioIso, fimIso, open);
  const amostras = query.data?.lista ?? [];

  const titulo = inicioBucket
    ? `${alvo ?? ''} · ${formatarIntervalo(inicioBucket, fimBucket)}`
    : alvo ?? '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-mono text-base">{titulo}</DialogTitle>
          <DialogDescription>
            Amostras observadas no intervalo do bucket — DERIVED reflete tráfego real, SYNTHETIC é probe ativo.
          </DialogDescription>
        </DialogHeader>

        {bucket && <ResumoBucket bucket={bucket} />}
        <ProbesSinteticos amostras={amostras} />

        <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 200px)' }}>
          {query.isLoading ? (
            <div className="py-8">
              <Spinner size={32} label="Carregando amostras" />
            </div>
          ) : amostras.length === 0 ? (
            <EmptyState
              title="Nenhuma amostra registrada"
              description="O bucket pode estar vazio (alvo sem tráfego e synthetic não rodou)."
            />
          ) : (
            <TabelaAmostras amostras={amostras} alvo={alvo} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Mostra a(s) URL(s) probadas pelos synthetics deste bucket. URL é fixa por
 * alvo, então normalmente é uma só — coletamos como Set para cobrir o caso
 * raro de mudança de config dentro do mesmo bucket horário.
 */
function ProbesSinteticos({ amostras }: { amostras: AmostraDetalhe[] }) {
  const urls = useMemo(() => {
    const set = new Set<string>();
    for (const a of amostras) {
      if (a.tipo !== 'SYNTHETIC') continue;
      const detalhe = parseDetalhe(a.detalhe);
      if (detalhe?.url) set.add(detalhe.url);
    }
    return [...set];
  }, [amostras]);

  if (urls.length === 0) return null;

  return (
    <div className="rounded border bg-muted/30 px-3 py-2 text-xs">
      <div className="mb-1 text-muted-foreground">
        Probe sintético {urls.length > 1 ? `(${urls.length} URLs no período)` : ''}
      </div>
      <ul className="space-y-0.5">
        {urls.map((url) => (
          <li key={url} className="flex items-center gap-2">
            <span className="shrink-0 font-mono text-[11px] uppercase text-muted-foreground">GET</span>
            <span className="break-all font-mono text-xs" title={url}>
              {url}
            </span>
            <CopyButton text={url} label="Copiar URL do probe" className="ml-auto h-6 w-6 shrink-0" />
          </li>
        ))}
      </ul>
    </div>
  );
}

function ResumoBucket({ bucket }: { bucket: BucketSerie }) {
  const status: StatusAlvo = bucket.status_dominante;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded border bg-muted/30 px-3 py-2 text-xs">
      <MonitoramentoStatusPill status={status} />
      <span className="text-muted-foreground">
        {bucket.total} amostra{bucket.total === 1 ? '' : 's'}
      </span>
      <span>
        <span className="text-green-700 dark:text-green-400">↑ {bucket.count_up}</span>
        {' · '}
        <span className="text-yellow-700 dark:text-yellow-400">~ {bucket.count_degraded}</span>
        {' · '}
        <span className="text-red-700 dark:text-red-400">↓ {bucket.count_down}</span>
      </span>
      {bucket.p95_ms !== null && (
        <span className="text-muted-foreground">p95 {bucket.p95_ms}ms</span>
      )}
    </div>
  );
}

function TabelaAmostras({
  amostras,
  alvo,
}: {
  amostras: AmostraDetalhe[];
  alvo: string | null;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[150px]">Quando</TableHead>
          <TableHead className="w-[80px]">Tipo</TableHead>
          <TableHead className="w-[110px]">Status</TableHead>
          <TableHead className="w-[80px] text-right">Latência</TableHead>
          <TableHead>Operação / erro</TableHead>
          <TableHead className="w-[60px]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {amostras.map((a, idx) => (
          <LinhaAmostra key={idx} amostra={a} alvo={alvo} />
        ))}
      </TableBody>
    </Table>
  );
}

function LinhaAmostra({
  amostra,
  alvo,
}: {
  amostra: AmostraDetalhe;
  alvo: string | null;
}) {
  const navigate = useNavigate();
  const detalhe = useMemo(() => parseDetalhe(amostra.detalhe), [amostra.detalhe]);
  const reqId = detalhe?.reqId ?? null;
  const operation = detalhe?.operation ?? null;
  const erroMsg = detalhe?.erro ?? amostra.codigo_erro;

  const irParaLogs = () => {
    if (!reqId) return;
    const params = new URLSearchParams({ requestId: reqId });
    if (alvo) params.set('alvo', alvo);
    navigate(`/app/admin/observability?${params.toString()}`);
  };

  return (
    <TableRow>
      <TableCell className="text-xs">
        {timeOnly(amostra.observado_em, true)}
      </TableCell>
      <TableCell>
        <span
          className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
            amostra.tipo === 'DERIVED'
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
          }`}
        >
          {amostra.tipo}
        </span>
      </TableCell>
      <TableCell>
        <MonitoramentoStatusPill status={amostra.status} />
      </TableCell>
      <TableCell className="text-right text-xs">
        {amostra.latencia_ms !== null ? `${amostra.latencia_ms}ms` : '—'}
      </TableCell>
      <TableCell className="text-xs">
        {operation && <span className="font-mono">{operation}</span>}
        {operation && erroMsg && <span className="text-muted-foreground"> · </span>}
        {erroMsg && <span className="text-muted-foreground">{erroMsg}</span>}
        {!operation && !erroMsg && <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell>
        {reqId && (
          <button
            type="button"
            onClick={irParaLogs}
            className="cursor-pointer text-muted-foreground hover:text-foreground"
            aria-label={`Ver logs do request ${reqId}`}
            title={`Ver logs (request ${reqId.slice(0, 8)})`}
          >
            <ExternalLink className="size-3.5" />
          </button>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * Formata o título do bucket como "DD/MM/AAAA, HH:MM – HH:MM" quando início e
 * fim caem no mesmo dia (caso quase universal: bucket horário de 1h). Se
 * cruzar a meia-noite, mostra a data nos dois lados para evitar ambiguidade.
 */
function formatarIntervalo(inicio: Date, fim: Date | null): string {
  if (!fim) return `${dateOnly(inicio)}, ${timeOnly(inicio)}`;
  const mesmaData =
    inicio.getFullYear() === fim.getFullYear() &&
    inicio.getMonth() === fim.getMonth() &&
    inicio.getDate() === fim.getDate();
  if (mesmaData) {
    return `${dateOnly(inicio)}, ${timeOnly(inicio)} – ${timeOnly(fim)}`;
  }
  return `${dateOnly(inicio)} ${timeOnly(inicio)} – ${dateOnly(fim)} ${timeOnly(fim)}`;
}

interface DetalheAmostra {
  operation?: string;
  reqId?: string;
  erro?: string;
  // SYNTHETIC: URL probada (ex.: https://comunicaapi.pje.jus.br/.../actuator/health).
  // Para o probe self-ping do autenticador é o sentinel "internal:db+redis"
  // — mostra que o probe não é HTTP, é checagem interna.
  url?: string;
}

function parseDetalhe(raw: string | null): DetalheAmostra | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw) as Record<string, unknown>;
    return {
      operation: typeof obj.operation === 'string' ? obj.operation : undefined,
      reqId: typeof obj.reqId === 'string' ? obj.reqId : undefined,
      erro: typeof obj.erro === 'string' ? obj.erro : undefined,
      url: typeof obj.url === 'string' ? obj.url : undefined,
    };
  } catch {
    return null;
  }
}
